import { type Dungeon, type Vec2, cellCentre, vec2 } from '@d2/core';
import { type BodyAction } from '@d2/schema';
import Phaser from 'phaser';

import { RESTING_MARK_TEXTURE, actionLook, markRise } from './action-art';
import {
  CAVE_TILESET,
  ENTITY_OTHER_TEXTURE,
  ENTITY_SIZE,
  ENTITY_TEXTURE,
  FLOOR_TILE,
  GROUND_ITEM_RISE,
  GROUND_ITEM_TEXTURE,
  MONSTER_BAR_BORDER,
  MONSTER_BAR_FILL,
  MONSTER_BAR_FILL_TEXTURE,
  MONSTER_BAR_RISE,
  MONSTER_BAR_TEXTURE,
  MONSTER_ORIGIN,
  MONSTER_SIZE,
  ROCK_RISE,
  ROCK_TILE,
  STAIRS_BEACON_TEXTURE,
  STAIRS_TEXTURE,
  TILESET_MARGIN,
  TILESET_SPACING,
  TILE_FRAME,
  TILE_ORIGIN,
  TILE_SPRITES,
} from './art-spec';
import { edgeBeacon } from './beacon';
import { type Viewport, dragCamera, stepCamera } from './camera';
import { type DrawnFloor, splitCaveTiles } from './cave';
import { groundFill } from './ground-art';
import { ISO_TILE, bandDepth, cellToScreen, gridToScreen, isoDepth, screenToCell, stairsDepth } from './iso';
import { badgeOf, lifeBarPixels, monsterTexture } from './monster-art';
import { generatePlaceholderArt } from './placeholder-art';
import { type Gesture, beginGesture, isClick, moveGesture } from './pointer-gesture';
import { type DrawnMonster, type GroundItemSnapshot, type MonsterSnapshot } from './session';
import { type CursorTarget, cursorFor, sameTarget, targetAt } from './targeting';
import { type EntityPositions, type WorldView } from './world-view';

/**
 * Draws a cave and everybody standing in it, isometrically. Decides nothing.
 *
 * A class because `Phaser.Scene` is the framework's extension point — Phaser
 * instantiates and drives it — and that is the only reason. Everything with a
 * rule in it lives elsewhere: the cave arrives already grown and already
 * checksummed against the server's (`./cave`), the cell↔pixel maths is
 * `./iso`'s, where every entity *is* belongs to `./world-view` and the
 * prediction, interpolation and clock behind it, click-versus-drag is
 * `./pointer-gesture`'s, and the artwork is `./placeholder-art`'s. What is left
 * here is placing tiles, pointing a camera, forwarding input, and moving one
 * sprite per entity to wherever it was told.
 *
 * ## The scene holds no position
 *
 * It used to hold a `Motion` and walk it. It now holds a `WorldView` and asks
 * it, once a frame, where everything is — because the answer for the local
 * player is a prediction reconciled against the server, the answer for everyone
 * else is an interpolation buffer a hundred milliseconds behind, and neither is
 * a renderer's business. A click is forwarded as a *cell*, not as a route: the
 * pixel→cell conversion is the one piece of that pipeline that genuinely is
 * this side of the boundary.
 *
 * ## Entities are named by session id
 *
 * `spawnEntity` used to hand back an index into an array, which meant an entity
 * id was the renderer's own invention and was not even stable across a scene
 * restart (`TODO.md` #122, #150). Sprites are now filed under the room's own
 * `sessionId` — the same key `WorldState.players` uses and the same key the
 * interpolation buffers use — so nothing has to be reconciled and a player who
 * leaves takes their capsule with them. The map is emptied in `create`, because
 * a class field initialiser survives `scene.restart()` and the sprites in it do
 * not.
 *
 * ## Monsters get the same discipline, under the room's own ids
 *
 * A floor carries sixty of them and they die, so the lifecycle is the part that
 * has to be right: the sprites for a monster are filed under the id
 * `WorldState.monsters` keys it by, the set of sprites is *derived* from the
 * answer `positionsAt` gives each frame rather than maintained beside it, and a
 * body the room has stopped replicating has its three display objects destroyed
 * on the very next frame. There is no death message and no despawn path of its
 * own: a monster that is not in the snapshot is not on the floor, which is the
 * same rule a player who left obeys and the reason neither can leak a sprite.
 *
 * Four objects rather than one, because a life bar changes width and a body does
 * not: the body carries the archetype and the elite standing as a baked texture,
 * the trough and the fill are shared textures placed above its head, and the
 * mark says what it is doing. All four take the same depth from the same
 * position, so a bar can never sort away from the body it belongs to.
 *
 * ## What a body is doing is a fourth object, not a fifth texture axis
 *
 * A monster's body is already one of twelve baked textures — three archetypes by
 * four standings — and folding eight actions into that key would be
 * ninety-six. So an action is drawn *beside* the body instead: one extra image
 * per body, holding the mark `action-art.ts` decides on, at the same depth as
 * the body and added after it so it lands on top.
 *
 * Every body carries that image from the moment it is created, hidden while
 * there is nothing to say — the same shape as the life bar's fill, which exists
 * and is hidden at zero life. Creating it on demand would make the display list
 * depend on what has happened rather than on what is on the floor, and *"a
 * monster that dies leaves no sprite behind"* is asserted by counting that list.
 *
 * The body itself is never nudged, leaned or scaled by an action, and that is a
 * correctness constraint rather than restraint: `targeting.ts` hit-tests a body
 * from its **position**, so a sprite moved for a lunge would be a monster the
 * player can see and cannot click. A corpse is the one exception, and a corpse
 * is not a target.
 *
 * ## What a left click means
 *
 * D2's mapping, and the whole of what this scene decides about a click: a body
 * under the cursor is a swing, a drop under the cursor is a pickup, the way down
 * under the cursor is a descent, and anything else is a walk. *Which* of those
 * it is is `./targeting`'s answer, because it is a question about pixels; what
 * may be **done** about it is `admitSwing`'s and the room's, and the scene
 * forwards an intent and predicts nothing. The cursor changes to say which of
 * the four a click would be, which is worth more than a button: it tells the
 * player, where they are already looking, that the click will do something.
 *
 * ## The way down is drawn twice, and the second one is why it can be found
 *
 * The stairs sit at maximum flow distance from the entrance — up to a hundred
 * and eighty cells on a 128×128 floor, against a camera that shows about twenty
 * across — so for almost the whole of a run the stairwell on its own cell is a
 * sprite nobody can see. It is drawn there anyway, because you have to see what
 * you click; and an **arrow is pinned to the edge of the view** pointing at it
 * whenever it is off screen, which is what makes the exit findable at all. Where
 * that arrow goes and which way it points is `./beacon`'s, pure and testable
 * without a canvas; this places one image and turns it.
 *
 * The arrow is the one thing in this scene drawn in *screen* space rather than
 * on the floor — it follows the camera rather than the ground — so it carries a
 * depth above every band of rock, and it is the only display object here that
 * moves when nothing in the world did.
 *
 * ## A descent redraws everything, through `create`
 *
 * A floor is no longer decided once. `WorldView.floor` is a value replaced whole
 * when the room rebuilds the level, so `update` compares the floor it laid out
 * against the one the view is holding — **by identity**, which cannot be wrong —
 * and runs `scene.restart()` when they differ. That is Phaser's own path for
 * "throw this scene's display list away and build it again", and `create`
 * already empties every sprite map for exactly that reason. Nothing here has to
 * know what a descent is: the ground changed, so the scene is rebuilt.
 *
 * ## Depth sorting
 *
 * In an isometric view the drawing order *is* the geometry: whatever is drawn
 * last is nearest. Sorting by `x + y` gets that exactly right, because that sum
 * is a position's screen `y`. Cells sharing a value of `x + y` — a diagonal
 * band — sit side by side on screen and never overlap each other, so any order
 * within a band is safe. That is the whole rule, and `iso.ts` owns it.
 *
 * So **every entity carries its own depth**, straight from its own position,
 * and rock is laid out once as one layer per band carrying that band's depth.
 * Phaser sorts the two against each other every frame, which is exactly the
 * painter's algorithm and needs no help from here: an entity that walks a band
 * forward simply passes the layer it used to be behind. Nothing is re-split and
 * nothing counts entities, so a fourth player or a pack of monsters costs a
 * sprite each and not one layer (TODO row 61).
 *
 * The floor never takes part: it is flat, tiles exactly, and so can never
 * occlude anything. It just sits underneath, and is the one thing still cheap
 * enough to leave as a tilemap.
 *
 * ## The camera decides nothing either
 *
 * It follows the local player and a drag pans it, bounded so the character can
 * never leave the view (`docs/GAME-DESIGN.md`, `TODO.md` #351) — and every one
 * of those words is `./camera`'s, which is pure and testable without a browser.
 * What is left here is the three things only a scene can do: read Phaser's
 * camera, hand `./camera` the frame's own duration, and write the answer back
 * with `centerOn`.
 *
 * The position it follows is `positions.local` — the **predicted** local
 * player. Every other body on the floor comes out of an interpolation buffer a
 * hundred milliseconds behind (`session.ts`), and pointing the camera at one of
 * those would spend that delay on the entire world rather than on one capsule:
 * the click would still start the walk immediately and the whole screen would
 * take a tenth of a second to agree, which reads as input lag and is invisible
 * in a screenshot.
 */

/**
 * Below every band and every entity, and never above anything.
 *
 * Negative because the shallowest band of rock is drawn at `bandDepth(0)` and
 * an entity standing on the grid origin at `0`; the floor has to be under both.
 */
const DEPTH_FLOOR = -1;

const FLOOR_LAYER = 'floor';

/**
 * Extra tiles kept alive beyond the camera's edge. Two, because the cull test
 * is a tile's base position while the frame drawn there is a whole tile taller
 * than its footprint — a tileset has one frame size, so the floor is cut to fit
 * a raised rock.
 */
const CULL_PADDING = 2;

/**
 * Above every band of rock and every entity, because it is not standing on the
 * floor at all: the beacon is pinned to the edge of the *view* and nothing in
 * the world may cover it. Larger than the deepest band a 512×512 cave could
 * reach, so it stays on top whatever size a floor is.
 */
const DEPTH_BEACON = 1e6;

/**
 * What a scene needs to draw: a way to ask where everyone is, and which floor
 * they are on.
 *
 * The cave used to be handed over here and is not any more. `scene.restart()`
 * does not run a constructor, so a floor captured at construction is the floor
 * the scene would still be drawing after a descent — see the header.
 */
export interface DungeonSceneInit {
  readonly view: WorldView;
}

/**
 * The four display objects one monster is drawn from.
 *
 * Held together so the lifecycle cannot half-happen: they are created together,
 * moved together, given one depth, and destroyed together. A bar that outlived
 * its body would be a floating number over an empty cell, and a mark that
 * outlived one would be a swing with nothing behind it.
 */
interface MonsterSprites {
  readonly body: Phaser.GameObjects.Image;
  readonly trough: Phaser.GameObjects.Image;
  readonly fill: Phaser.GameObjects.Image;
  /** What it is doing, hidden while it is doing nothing worth drawing. */
  readonly mark: Phaser.GameObjects.Image;
}

export class DungeonScene extends Phaser.Scene {
  public static readonly KEY = 'dungeon';

  private readonly view: WorldView;

  /**
   * The floor this scene has actually laid out.
   *
   * Compared against `view.floor` by **identity** once a frame: one floor is one
   * object and the only way to get another is a descent, so a mismatch is
   * exactly "the ground moved" and nothing else. A depth or a seed would work
   * until two floors of a run ever shared one.
   */
  private painted: DrawnFloor;

  /**
   * The arrow saying which way the stairs are, or `undefined` on a floor that
   * has none (the renderer sandbox).
   *
   * Created hidden with the floor rather than on demand, the same shape the life
   * bar's fill and every action mark have: a display list that depended on what
   * has happened rather than on what is on the floor is a display list nothing
   * can count.
   */
  private beacon: Phaser.GameObjects.Image | undefined;

  /**
   * One sprite per entity, keyed by the room's session id.
   *
   * A field initialiser, so it survives `scene.restart()` and the destroyed
   * images inside it do not — `create` empties it before adding anything, which
   * is what closes `TODO.md` #150.
   */
  private readonly sprites = new Map<string, Phaser.GameObjects.Image>();

  /**
   * What each player is *doing*, as a second image per body under the same key.
   *
   * A map beside the capsules rather than a field on them, because a player is
   * one image where a monster is a small record — and both are created,
   * positioned and destroyed in the same breath as the body they belong to, so
   * neither can outlive it.
   */
  private readonly playerMarks = new Map<string, Phaser.GameObjects.Image>();

  /** The same, for the floor's population, keyed by the room's own monster id. */
  private readonly monsterSprites = new Map<string, MonsterSprites>();

  /**
   * One sprite per drop, keyed by the room's own ground id.
   *
   * The same lifecycle as a monster's and for the same reason: a drop the room
   * has stopped replicating has been picked up — by this player or by a party
   * member — and is gone from the floor on the very next frame. There is no
   * pickup animation and no local removal; the snapshot is the truth about what
   * is lying there.
   */
  private readonly groundSprites = new Map<string, Phaser.GameObjects.Image>();

  /**
   * Where every entity was last *drawn*.
   *
   * Kept because a remote entity's position is a function of the clock: asking
   * the view again a millisecond later gives a different answer, and a caller
   * comparing a position against a screenshot has to be told the one that was
   * painted. The local player's is the same either way — a prediction only
   * moves when it is advanced — but it is kept here too so the two questions
   * have one answer.
   */
  private drawn: EntityPositions;

  /** The press in progress, if any. `undefined` between gestures. */
  private gesture: Gesture | undefined;

  /**
   * What the pointer is over, as of the last time it moved.
   *
   * Kept so the cursor is only written when the answer *changes* — a pointer
   * move fires many times a second and assigning a CSS property on every one of
   * them is a style recalculation the browser did not need — and so the browser
   * suite can ask what the renderer thinks is under the cursor without reading a
   * pixel.
   */
  private hovered: CursorTarget | null = null;

  public constructor({ view }: DungeonSceneInit) {
    super(DungeonScene.KEY);
    this.view = view;
    this.painted = view.floor;
    this.drawn = view.positionsAt(0);
  }

  public create(): void {
    generatePlaceholderArt(this);

    // Read here rather than in the constructor, because this is the method a
    // descent re-runs: `scene.restart()` shuts the scene down and starts it
    // again without ever touching the instance that was built.
    this.painted = this.view.floor;

    const tiles = splitCaveTiles(this.painted.cave.grid);

    const map = new Phaser.Tilemaps.Tilemap(
      this,
      new Phaser.Tilemaps.MapData({
        width: this.painted.cave.grid.width,
        height: this.painted.cave.grid.height,
        tileWidth: ISO_TILE.width,
        tileHeight: ISO_TILE.height,
        orientation: Phaser.Tilemaps.Orientation.ISOMETRIC,
      }),
    );

    // Frames are taller than the grid's tiles, and the matching tile offset is
    // what drops a frame's bottom band onto the cell it belongs to.
    const tileset = map.addTilesetImage(
      CAVE_TILESET,
      CAVE_TILESET,
      TILE_FRAME.width,
      TILE_FRAME.height,
      TILESET_MARGIN,
      TILESET_SPACING,
      FLOOR_TILE,
      { x: 0, y: ROCK_RISE },
    );

    if (tileset === null) {
      throw new Error(`Tileset "${CAVE_TILESET}" is missing its texture`);
    }

    const floor = this.addFloorLayer(map, tileset);

    for (const cell of tiles.floor) floor.putTileAt(FLOOR_TILE, cell.x, cell.y, false);
    this.drawRock(tiles.rockByBand);

    // Every sprite from a previous create is already destroyed; the maps that
    // held them are class fields and are not. The beacon is a plain field and is
    // destroyed with the display list, so it is simply forgotten and remade.
    this.sprites.clear();
    this.playerMarks.clear();
    this.monsterSprites.clear();
    this.groundSprites.clear();
    this.beacon = undefined;
    this.hovered = null;
    this.input.setDefaultCursor(cursorFor(null));
    this.drawStairs();
    this.paint(this.view.positionsAt(0));

    // On the player, not on the entrance. They are the same cell on the first
    // frame of a fresh room and they are not the same cell after a
    // reconnection or a `scene.restart()` part way through a walk — and "the
    // camera looks at your character" is the rule, of which "the way in is on
    // screen when you arrive" is a consequence.
    const start = gridToScreen(this.drawn.local);
    this.cameras.main.centerOn(start.x, start.y);

    this.input.on(Phaser.Input.Events.POINTER_DOWN, this.onPointerDown);
    this.input.on(Phaser.Input.Events.POINTER_MOVE, this.onPointerMove);
    this.input.on(Phaser.Input.Events.POINTER_UP, this.onPointerUp);
    this.input.on(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.onPointerUpOutside);
  }

  /**
   * One frame: let the world spend the time that has passed, then draw where
   * that leaves everybody.
   *
   * The clock is read once, here, and handed to both calls — so the local
   * player's predicted step and every remote entity's interpolated position
   * describe the same instant. Phaser's `delta` goes with it and is used only
   * when nothing has been heard from a server: how much simulation a frame is
   * worth is the *server's* tick rate mapped onto this browser's clock
   * wherever there is a server to ask.
   *
   * The camera is steered last, from the position that was just painted, so it
   * follows what is on screen this frame rather than what was on screen last
   * one.
   */
  public override update(_time: number, delta: number): void {
    // The ground first, and before anything is advanced or painted: a descent
    // has replaced the cave, the mover and the whole session, so a frame drawn
    // against the floor this scene laid out would be drawing bodies from one
    // cave onto the tiles of another. `create` runs again and this method is
    // finished for the frame.
    if (this.view.floor !== this.painted) {
      this.scene.restart();
      return;
    }

    const now = performance.now();

    this.view.advance(now, delta);
    this.paint(this.view.positionsAt(now));
    this.steerCamera(delta);
    this.steerBeacon();
  }

  /** The cave on screen. Read-only: the room's seed decides it, and nothing here may edit it. */
  public get dungeon(): Dungeon {
    return this.painted.cave;
  }

  /** The way down on the floor being drawn, or `null` on one that has none. */
  public get stairs(): Vec2 | null {
    return this.painted.stairs;
  }

  /** The entity this client drives. */
  public get localId(): string {
    return this.view.localId;
  }

  /** Where the local player was last drawn, as a continuous grid position. */
  public get entityPosition(): Vec2 {
    return this.drawn.local;
  }

  /** Where another entity was last drawn, or `undefined` when nothing is on the map under that id. */
  public readonly entityPositionOf = (id: string): Vec2 | undefined =>
    id === this.view.localId ? this.drawn.local : this.drawn.remotes.get(id);

  /** Every entity on the map right now, by session id. */
  public readonly entityIds = (): readonly string[] => [this.view.localId, ...this.drawn.remotes.keys()];

  /** Where a cell sits on screen. The renderer owns pixels, so it answers this for everyone else. */
  public readonly worldPositionOf = (cell: Vec2): Vec2 => cellToScreen(cell);

  /**
   * Stands the local player at a **continuous position**, abandoning any walk.
   *
   * Continuous, not a cell (TODO row 80). A walker's position is a point part
   * way between two cells, and quantising it back onto a cell centre here would
   * render a smooth walk as a cell-by-cell teleport.
   *
   * The renderer sandbox's door, and only that: in a room the server owns where
   * this entity is, and a placement is undone by the next snapshot. See
   * `session.ts`'s `placeLocal` for why it exists at all.
   */
  public readonly moveEntityTo = (position: Vec2): void => {
    this.view.placeLocal(position);
    this.paint(this.view.positionsAt(performance.now()));
  };

  /**
   * Puts another entity on the map under a name of the caller's choosing, and
   * hands the name back.
   *
   * The same sandbox door as `moveEntityTo`. A live room adds and removes these
   * by itself, from the session ids in its snapshots.
   */
  public readonly spawnEntity = (position: Vec2, id?: string): string => {
    const name = id ?? `sandbox:${this.sprites.size}`;

    this.view.placeRemote(name, position);
    this.paint(this.view.positionsAt(performance.now()));

    return name;
  };

  /** Moves an entity the map already holds. The sandbox counterpart of a replicated position. */
  public readonly moveEntity = (id: string, position: Vec2): void => {
    this.view.placeRemote(id, position);
    this.paint(this.view.positionsAt(performance.now()));
  };

  /**
   * Says what a player — this client's own body included — is doing.
   *
   * The same sandbox door as `moveEntityTo`, and it exists because three of the
   * eight states cannot be asked of a live room: `blocking` has no rule behind
   * it yet (`TODO.md` #32), `casting` is invisible for eight of the nine skills
   * (`TODO.md` #483), and a corpse costs a character. A room writes all of this
   * from its own snapshots and overwrites anything put here — `winding-up`
   * included, which a live floor produces on seven ticks of every swing.
   */
  public readonly actEntity = (id: string, action: BodyAction): void => {
    this.view.actEntity(id, action);
    this.paint(this.view.positionsAt(performance.now()));
  };

  /** Takes an entity off the map, sprite and all. */
  public readonly removeEntity = (id: string): void => {
    this.view.forgetRemote(id);
    this.paint(this.view.positionsAt(performance.now()));
  };

  /**
   * Puts a monster on the floor, described — the sandbox door for a population
   * a live room rolls for itself.
   *
   * The whole description rather than a position, because what has to be
   * arranged on demand is a *champion*, a *boss*, a body at a quarter life: none
   * of those can be asked of a room and all of them have to be drawable.
   */
  public readonly spawnMonster = (monster: MonsterSnapshot): string => {
    this.view.placeMonster(monster);
    this.paint(this.view.positionsAt(performance.now()));

    return monster.id;
  };

  /** Takes a monster off the floor, sprite and all. What a death looks like from here. */
  public readonly removeMonster = (id: string): void => {
    this.view.forgetMonster(id);
    this.paint(this.view.positionsAt(performance.now()));
  };

  /** Every monster the scene is currently drawing, by the room's own id. */
  public readonly monsterIds = (): readonly string[] => [...this.drawn.monsters.keys()];

  /** What was last *drawn* for a monster, or `undefined` when nothing is on the floor under that id. */
  public readonly monsterDrawnAt = (id: string): DrawnMonster | undefined => this.drawn.monsters.get(id);

  /**
   * Lays a drop on the floor — the sandbox door for loot a live room only
   * produces by killing something.
   *
   * The whole description rather than a cell, for `spawnMonster`'s reason: what
   * has to be arranged on demand is a *unique*, a pile of coins, a respec
   * scroll, three drops on one square.
   */
  public readonly spawnGroundItem = (item: GroundItemSnapshot): string => {
    this.view.placeGroundItem(item);
    this.paint(this.view.positionsAt(performance.now()));

    return item.id;
  };

  /** Takes a drop off the floor, sprite and all. What a pickup looks like from here. */
  public readonly removeGroundItem = (id: string): void => {
    this.view.forgetGroundItem(id);
    this.paint(this.view.positionsAt(performance.now()));
  };

  /** Every drop the scene is currently drawing, by the room's own id. */
  public readonly groundItemIds = (): readonly string[] => [...this.drawn.ground.keys()];

  /**
   * What a click at this **canvas** point would act on, or `null` for open
   * floor.
   *
   * The scene's own answer, exposed because it is the one thing about targeting
   * a screenshot cannot show: a browser test can assert that the pointer is over
   * a body without knowing which pixel the body's silhouette happens to reach.
   */
  public readonly targetUnder = (x: number, y: number): CursorTarget | null =>
    targetAt(this.worldPoint(x, y), this.drawn, this.painted.stairs);

  /** What the pointer last resolved to. The affordance, as a value. */
  public get hoveredTarget(): CursorTarget | null {
    return this.hovered;
  }

  /**
   * Draws one frame's worth of entities: add whoever is new, drop whoever has
   * gone, move the rest.
   *
   * The set of sprites is derived from the answer rather than maintained
   * alongside it, so a player who left cannot leave a capsule behind and a
   * player who joined cannot fail to get one. That is the same reasoning
   * `session.ts` rebuilds its buffers by, one layer down.
   *
   * Each entity's depth is set on every move, because its `x + y` *is* its
   * screen `y` — so Phaser sorting the sprites by it sorts them exactly as the
   * eye would, against the rock and against each other.
   */
  private readonly paint = (positions: EntityPositions): void => {
    this.drawn = positions;

    for (const id of this.sprites.keys()) {
      if (id !== this.view.localId && !positions.remotes.has(id)) {
        this.sprites.get(id)?.destroy();
        this.sprites.delete(id);
        this.playerMarks.get(id)?.destroy();
        this.playerMarks.delete(id);
      }
    }

    for (const id of this.monsterSprites.keys()) {
      if (!positions.monsters.has(id)) this.buryMonster(id);
    }

    for (const id of this.groundSprites.keys()) {
      if (!positions.ground.has(id)) {
        this.groundSprites.get(id)?.destroy();
        this.groundSprites.delete(id);
      }
    }

    // Loot first, so a body standing on it is painted over it — which is the
    // order `targeting.ts` breaks a depth tie in, said once in each place it has
    // to be true.
    for (const [id, item] of positions.ground) this.placeGroundItem(id, item);
    this.place(this.view.localId, positions.local, ENTITY_TEXTURE, positions.actions.get(this.view.localId));
    for (const [id, position] of positions.remotes) {
      this.place(id, position, ENTITY_OTHER_TEXTURE, positions.actions.get(id));
    }
    for (const [id, monster] of positions.monsters) this.placeMonster(id, monster);
  };

  /**
   * One player: the capsule where it stands, and the mark for whatever it is
   * doing, both at the depth its own position gives them.
   *
   * The mark is created with the body and after it, so at their shared depth
   * Phaser's stable sort always draws it over the capsule it belongs to — the
   * same ordering the life bar depends on one method down.
   */
  private readonly place = (id: string, position: Vec2, texture: string, action: BodyAction | undefined): void => {
    const sprite = this.sprites.get(id) ?? this.add.image(0, 0, texture).setOrigin(0.5, 1);
    const mark = this.playerMarks.get(id) ?? this.addMark();
    const screen = gridToScreen(position);
    const depth = isoDepth(position);

    this.sprites.set(id, sprite);
    this.playerMarks.set(id, mark);
    sprite.setPosition(screen.x, screen.y).setDepth(depth);
    this.showAction(sprite, mark, action, screen, depth, ENTITY_SIZE.height);
  };

  /**
   * Draws what one body is doing: the mark over its shoulders, and — for a
   * corpse, and only a corpse — the body itself turned onto the floor.
   *
   * Shared by players and monsters because it is the same decision for both, and
   * the only thing that differs is how tall the body is: `action-art.ts` places
   * a mark as a fraction of that, so one row of monsters and one row of players
   * each show their marks in a line rather than a staircase.
   *
   * The texture is re-read every frame and only *set* when it has changed, for
   * the reason a monster's body texture is: an action changes several times a
   * second in a fight, and an assignment per frame per body is sixty texture
   * swaps a frame for a value that moved once.
   *
   * **The depth is written whether or not there is anything to show**, exactly
   * as the life bar's fill writes its own at zero life. That is not tidiness: a
   * hidden mark left at the depth it was created with sinks below the body it
   * belongs to, Phaser's depth sort is *stable*, and so it stays below it for
   * ever afterwards — the mark is then drawn, correctly positioned, and
   * completely invisible behind the body it is describing. Which is precisely
   * what happened the first time this ran.
   */
  private readonly showAction = (
    body: Phaser.GameObjects.Image,
    mark: Phaser.GameObjects.Image,
    action: BodyAction | undefined,
    screen: Vec2,
    depth: number,
    bodyHeight: number,
  ): void => {
    const look = actionLook(action);

    body.setAngle(look.angle);

    if (look.mark !== null && mark.texture.key !== look.mark.key) mark.setTexture(look.mark.key);

    mark
      .setPosition(screen.x, screen.y - (look.mark === null ? 0 : markRise(look.mark, bodyHeight)))
      .setDepth(depth)
      .setVisible(look.mark !== null);
  };

  /** A body's action mark: created hidden, because a body starts out doing nothing. */
  private readonly addMark = (): Phaser.GameObjects.Image =>
    this.add.image(0, 0, RESTING_MARK_TEXTURE).setOrigin(0.5, 0.5).setVisible(false);

  /**
   * Draws one monster: its body where it stands, its bar over its head, and all
   * three objects at the one depth its own position gives it.
   *
   * The texture is re-read from the identity every frame and only *set* when it
   * has changed, because a body's standing is not a constant — a champion tier
   * or an affix mask reaches this client in the same patch the body does, and
   * the first frame after a join can legitimately draw a monster before the
   * whole of what it is has arrived.
   *
   * The fill is stretched to whatever the life band is worth, and hidden
   * outright at nothing. That is the "empty bar means dead" contract kept in
   * pixels: the trough stays, so an empty bar reads as empty rather than as a
   * monster whose bar failed to draw.
   */
  private readonly placeMonster = (id: string, monster: DrawnMonster): void => {
    const texture = monsterTexture(monster.identity.archetype, badgeOf(monster.identity));
    const sprites = this.monsterSprites.get(id) ?? this.addMonster(texture);
    const screen = gridToScreen(monster.position);
    const depth = isoDepth(monster.position);
    const filled = lifeBarPixels(monster.identity.life);

    this.monsterSprites.set(id, sprites);

    if (sprites.body.texture.key !== texture) sprites.body.setTexture(texture);
    sprites.body.setPosition(screen.x, screen.y).setDepth(depth);

    sprites.trough.setPosition(screen.x, screen.y - MONSTER_BAR_RISE).setDepth(depth);
    sprites.fill
      .setPosition(screen.x - MONSTER_BAR_FILL.width / 2, screen.y - MONSTER_BAR_RISE - MONSTER_BAR_BORDER)
      .setDepth(depth)
      .setVisible(filled > 0);

    // Stretched, not cropped: the fill is one flat colour drawn with
    // nearest-neighbour filtering, so a band's worth of pixels is exact.
    if (filled > 0) sprites.fill.setDisplaySize(filled, MONSTER_BAR_FILL.height);

    this.showAction(sprites.body, sprites.mark, monster.identity.action, screen, depth, MONSTER_SIZE.height);
  };

  /**
   * A monster's four display objects, in drawing order.
   *
   * The order they are added in is the order they sit in the display list, and
   * Phaser's depth sort is stable — so at the one depth they share, the bar and
   * the action mark are always drawn over the body they belong to.
   */
  private readonly addMonster = (texture: string): MonsterSprites => ({
    body: this.add.image(0, 0, texture).setOrigin(MONSTER_ORIGIN.x, MONSTER_ORIGIN.y),
    trough: this.add.image(0, 0, MONSTER_BAR_TEXTURE).setOrigin(0.5, 1),
    fill: this.add.image(0, 0, MONSTER_BAR_FILL_TEXTURE).setOrigin(0, 1),
    mark: this.addMark(),
  });

  /**
   * Draws one drop: a lozenge on its cell, tinted by what it is.
   *
   * The tint is re-read every frame and only *set* when it has changed, for the
   * reason a monster's texture is: a pile of coins a capped purse could only
   * part-take keeps its id and its cell and changes what it holds, so an entry
   * is not a constant even though a drop never moves.
   *
   * The depth is its cell's, which puts it in the same band as anything standing
   * on that cell — a tie broken by paint order in `paint`, and by the same rule
   * in `targeting.ts`.
   */
  private readonly placeGroundItem = (id: string, item: GroundItemSnapshot): void => {
    const sprite = this.groundSprites.get(id) ?? this.add.image(0, 0, GROUND_ITEM_TEXTURE).setOrigin(0.5, 0.5);
    const screen = cellToScreen(item.cell);
    const fill = groundFill(item);

    this.groundSprites.set(id, sprite);

    if (sprite.tintTopLeft !== fill) sprite.setTint(fill);
    sprite.setPosition(screen.x, screen.y - GROUND_ITEM_RISE).setDepth(isoDepth(cellCentre(item.cell)));
  };

  /** Destroys everything drawn for a monster. Called the frame the room stops replicating it. */
  private readonly buryMonster = (id: string): void => {
    const sprites = this.monsterSprites.get(id);
    if (sprites === undefined) return;

    sprites.body.destroy();
    sprites.trough.destroy();
    sprites.fill.destroy();
    sprites.mark.destroy();
    this.monsterSprites.delete(id);
  };

  /**
   * The way down, laid out once with the floor: the well on its own cell, and
   * the arrow that will point at it from the edge of the view.
   *
   * The well is drawn at the cell's own depth, which puts it in the same band as
   * anything standing on that cell — a tie broken by paint order, since `paint`
   * draws loot and then bodies over it, and by the same rule in `targeting.ts`.
   * It is created *before* them for exactly that reason.
   *
   * Nothing at all on a floor with no way down. That is the renderer sandbox and
   * only the sandbox: a room's floor is refused outright unless its stairs
   * verify (`./cave`), so a connected client always has both.
   */
  private readonly drawStairs = (): void => {
    const cell = this.painted.stairs;
    if (cell === null) return;

    const screen = cellToScreen(cell);

    // Lifted clear of the rock it is set into rather than drawn at its own
    // cell's depth — `iso.ts`'s `stairsDepth` argues why an exit is the one
    // piece of terrain the depth sort has to be overruled for, and what that
    // costs. The click is *not* lifted with it (`targeting.ts`).
    this.add.image(screen.x, screen.y, STAIRS_TEXTURE).setOrigin(0.5, 0.5).setDepth(stairsDepth(cell));

    // Hidden until a frame finds the stairs off screen, exactly as an action
    // mark is hidden until a body is doing something: a display list that
    // depended on where the camera happens to be is a display list nothing can
    // count.
    this.beacon = this.add
      .image(0, 0, STAIRS_BEACON_TEXTURE)
      .setOrigin(0.5, 0.5)
      .setDepth(DEPTH_BEACON)
      .setVisible(false);
  };

  /**
   * One frame of the beacon: where the edge of the view meets the line to the
   * stairs, turned to point along it.
   *
   * Steered after the camera and never before it, for the reason the camera is
   * steered from the position that was just painted: the arrow is pinned to the
   * *view*, so a frame that placed it against last frame's centre would trail
   * the edge it is supposed to be sitting on by however far the camera moved.
   *
   * Where it goes is `./beacon`'s and none of it is decided here — including
   * `null`, which means the stairs are on screen and the well itself is saying
   * where they are.
   */
  private readonly steerBeacon = (): void => {
    const arrow = this.beacon;
    const cell = this.painted.stairs;
    if (arrow === undefined || cell === null) return;

    const mark = edgeBeacon(cellToScreen(cell), { centre: this.cameraCentre(), viewport: this.viewport() });

    if (mark === null) {
      arrow.setVisible(false);
      return;
    }

    arrow.setPosition(mark.position.x, mark.position.y).setAngle(mark.angle).setVisible(true);
  };

  private readonly addFloorLayer = (
    map: Phaser.Tilemaps.Tilemap,
    tileset: Phaser.Tilemaps.Tileset,
  ): Phaser.Tilemaps.TilemapLayer => {
    // Phaser positions an isometric tile by the top-left of its frame, so the
    // layer is shifted half a tile left to put cell centres where `iso.ts` says
    // they are. That half-tile lives here, not in the projection.
    const layer = map.createBlankLayer(FLOOR_LAYER, tileset, -ISO_TILE.width / 2, 0);

    if (layer === null) {
      throw new Error(`Tilemap layer "${FLOOR_LAYER}" already exists`);
    }

    layer.setDepth(DEPTH_FLOOR);
    layer.setCullPadding(CULL_PADDING, CULL_PADDING);

    return layer;
  };

  /**
   * Lays the rock out once: a sprite per solid cell, and one layer per band
   * holding the sprites of that band at the band's depth.
   *
   * A band is safe as a single display object because its cells cannot overlap
   * each other on screen (`iso.ts`), and it has to be a layer rather than a
   * tilemap layer because a tilemap culls every tile of the whole grid on every
   * frame — bearable for one layer, not for one per band.
   *
   * Nothing here is touched again. Entities sort themselves into this stack by
   * their own depth, which is the whole point: the number of layers answers to
   * the size of the map, never to how many things are standing on it.
   */
  private readonly drawRock = (rockByBand: readonly (readonly Vec2[])[]): void => {
    for (const [band, cells] of rockByBand.entries()) {
      if (cells.length === 0) continue;

      const tiles = cells.map((cell) => {
        const screen = cellToScreen(cell);

        return this.add.image(screen.x, screen.y, TILE_SPRITES, ROCK_TILE).setOrigin(TILE_ORIGIN.x, TILE_ORIGIN.y);
      });

      this.add.layer(tiles).setDepth(bandDepth(band));
    }
  };

  /**
   * One frame of camera: ease toward the player, or hold the ground a drag is
   * standing on — `./camera`'s decision, not this one's.
   *
   * Read and written through `scrollX + width / 2` and `centerOn`, which are
   * each other's exact inverse in Phaser, so the value handed out is the value
   * that comes back and the ease cannot drift against its own reading.
   */
  private readonly steerCamera = (frameMs: number): void => {
    const camera = this.cameras.main;
    const centre = stepCamera({
      centre: this.cameraCentre(),
      player: gridToScreen(this.drawn.local),
      viewport: this.viewport(),
      frameMs,
      held: this.gesture?.dragging === true,
    });

    camera.centerOn(centre.x, centre.y);
  };

  /** Where the camera is looking, in world pixels. */
  private readonly cameraCentre = (): Vec2 => {
    const camera = this.cameras.main;

    return vec2(camera.scrollX + camera.width / 2, camera.scrollY + camera.height / 2);
  };

  /**
   * How much world the camera can see, in world pixels.
   *
   * Read every frame rather than captured once: it is the canvas, and a canvas
   * can be resized. The zoom is divided out because a camera at twice the zoom
   * shows half the ground — nothing zooms this one, and the day something does,
   * `./camera` needs no changing for it.
   */
  private readonly viewport = (): Viewport => {
    const camera = this.cameras.main;

    return { width: camera.width / camera.zoom, height: camera.height / camera.zoom };
  };

  /**
   * A press starts a gesture that is a click until it proves otherwise.
   *
   * Left button only. The right button is unclaimed and will be a skill
   * (`docs/GAME-DESIGN.md`), so it must not pan the camera by accident.
   */
  private readonly onPointerDown = (pointer: Phaser.Input.Pointer): void => {
    if (!pointer.leftButtonDown()) return;

    this.gesture = beginGesture(vec2(pointer.x, pointer.y));
  };

  /**
   * Drag to pan — but only once the pointer has travelled far enough to be a
   * drag at all, which is what leaves a click free to mean "walk here"
   * (TODO row 62).
   *
   * A gesture whose button is no longer down is dropped rather than left to pan
   * on the next hover. `POINTER_UP_OUTSIDE` covers the ordinary release beyond
   * the canvas; this covers the release the browser never delivered at all,
   * which is what a drag out of the window and back looks like.
   *
   * The pan is bounded by `./camera` here and not merely on the next frame: a
   * fast drag delivers several moves between two frames, and "the character can
   * never leave the view" is a claim about where the camera *was*, not only
   * about where it was drawn.
   *
   * The threshold is untouched by any of this. It is measured between pointer
   * positions on the canvas (`./pointer-gesture`), which a moving camera cannot
   * change — so a camera that follows a walking player can never turn a click
   * into a drag or a drag into a click (`TODO.md` #62).
   */
  private readonly onPointerMove = (pointer: Phaser.Input.Pointer): void => {
    const gesture = this.gesture;

    // The affordance, and it is updated on every move whether or not a button is
    // down: what the cursor says is *what a click here would do*, which is a
    // question about where the pointer is and not about what it is doing.
    this.showTarget(this.targetUnder(pointer.x, pointer.y));

    if (gesture === undefined) return;

    if (!pointer.leftButtonDown()) {
      this.gesture = undefined;
      return;
    }

    const moved = moveGesture(gesture, vec2(pointer.x, pointer.y));
    const camera = this.cameras.main;
    const zoom = camera.zoom;

    this.gesture = moved.gesture;

    const centre = dragCamera({
      centre: this.cameraCentre(),
      player: gridToScreen(this.drawn.local),
      viewport: this.viewport(),
      pan: vec2(moved.pan.x / zoom, moved.pan.y / zoom),
    });

    camera.centerOn(centre.x, centre.y);
  };

  /**
   * A release that was never a drag is a click, and what is under it decides
   * what the click means: a body is a swing, a drop is a pickup, and the floor
   * is a walk.
   *
   * Every one of the three is forwarded as an **intent**, including the ones
   * this client can see will come to nothing — a click on rock, a body across
   * the map, a drop somebody else is standing over. That is not the renderer
   * being careless: the server resolves a walk with `nearestReachable` and walks
   * as close as it can, judges a swing with `admitSwing` and a pickup against
   * the floor it holds, so refusing any of them here would be the client quietly
   * deciding an outcome that is the server's to decide. What happens to a swing
   * out of reach is `./pursuit`'s answer — walk toward the body and ask again.
   */
  private readonly onPointerUp = (pointer: Phaser.Input.Pointer): void => {
    const gesture = this.gesture;
    if (gesture === undefined || !pointer.leftButtonReleased()) return;

    this.gesture = undefined;
    if (!isClick(gesture)) return;

    const point = this.worldPoint(pointer.x, pointer.y);
    const target = targetAt(point, this.drawn, this.painted.stairs);

    // Read once, here, and handed to the view: the frame's own clock, so the
    // body a pursuit walks toward is the body that was on screen when it was
    // clicked.
    if (target === null) {
      this.view.moveTo(screenToCell(point));
      return;
    }

    if (target.kind === 'monster') {
      this.view.attack(target.id, performance.now());
      return;
    }

    if (target.kind === 'stairs') {
      this.view.descend(performance.now());
      return;
    }

    this.view.pickUp(target.id);
  };

  /** A button released off the canvas ends the gesture and orders nothing. */
  private readonly onPointerUpOutside = (): void => {
    this.gesture = undefined;
  };

  /** A canvas point in world space — through the camera, which is where a click is aimed from. */
  private readonly worldPoint = (x: number, y: number): Vec2 => {
    const point = this.cameras.main.getWorldPoint(x, y);

    return vec2(point.x, point.y);
  };

  /**
   * Says, in the cursor, what a click would do here.
   *
   * Written only when the answer changes. A pointer move fires many times a
   * second and every assignment to `canvas.style.cursor` is a style
   * recalculation; the comparison is one identity check against the value the
   * last move resolved to.
   */
  private readonly showTarget = (target: CursorTarget | null): void => {
    if (sameTarget(this.hovered, target)) return;

    this.hovered = target;
    this.input.setDefaultCursor(cursorFor(target));
  };
}
